import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import React, { useRef, useEffect, useMemo, createContext } from 'react';
import { get, runAll, isFunction } from '@elliemae/ds-utilities/utils';
import useHotkeys from '@elliemae/ds-utilities/hooks/useHotkeys';
import { mergeRefs } from '@elliemae/ds-utilities/system';
import getNextCellPosition from './utils/getNextCellPosition.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var FocusGridContext = /*#__PURE__*/createContext();
var Provider = FocusGridContext.Provider;

var preventDefault = function preventDefault(e) {
  return e.preventDefault();
};

function registerHotKeysHooks(hotKeys, _ref) {
  var focusedRow = _ref.focusedRow,
      focusedCell = _ref.focusedCell;
  Object.keys(hotKeys).forEach(function (key) {
    var _hotKeys$key = hotKeys[key],
        _handler = _hotKeys$key.handler,
        options = _hotKeys$key.options;
    useHotkeys({
      keys: key,
      handler: function handler(e) {
        e.preventDefault();

        _handler({
          rowIndex: focusedRow.current,
          cellIndex: focusedCell.current
        });
      },
      options: options
    });
  });
} // eslint-disable-next-line max-statements


function FocusGridProvider(_ref2) {
  var _ref2$shouldWrapRows = _ref2.shouldWrapRows,
      shouldWrapRows = _ref2$shouldWrapRows === void 0 ? false : _ref2$shouldWrapRows,
      _ref2$shouldWrapCells = _ref2.shouldWrapCells,
      shouldWrapCells = _ref2$shouldWrapCells === void 0 ? false : _ref2$shouldWrapCells,
      _ref2$shouldRefocus = _ref2.shouldRefocus,
      shouldRefocus = _ref2$shouldRefocus === void 0 ? true : _ref2$shouldRefocus,
      children = _ref2.children,
      keyBindings = _ref2.keyBindings,
      hotKeys = _ref2.hotKeys;
  var focusedRow = useRef();
  var focusedCell = useRef();
  var grid = useRef([]);
  var containerRef = useRef(document);
  var child = React.Children.only(children);
  var decoratedChild = /*#__PURE__*/React.cloneElement(child, {
    innerRef: mergeRefs(child.props.innerRef, containerRef)
  });

  var getNode = function getNode(_ref3) {
    var rowIndex = _ref3.rowIndex,
        cellIndex = _ref3.cellIndex;
    return get(grid.current, [rowIndex, cellIndex]);
  };

  var register = function register(node, rowIndex, columnIndex) {
    if (!node) return;

    if (!Array.isArray(grid.current[rowIndex])) {
      grid.current[rowIndex] = [];
    }

    if (rowIndex === 0 && columnIndex === 0) {
      node.setAttribute('tabindex', 0);
    } else {
      node.setAttribute('tabindex', -1);
    }

    node.onfocus = function () {
      var prevNode = getNode({
        rowIndex: focusedRow.current,
        cellIndex: focusedCell.current
      });
      if (prevNode) prevNode.setAttribute('tabindex', -1);
      node.setAttribute('tabindex', 0);
      focusedRow.current = rowIndex;
      focusedCell.current = columnIndex;
    };

    grid.current[rowIndex][columnIndex] = node;
  };

  useEffect(function () {
    if (shouldRefocus) {
      var rowIndex = focusedRow.current;
      var cellIndex = focusedCell.current;
      var node = get(grid.current, [rowIndex, cellIndex]);
      if (node) node.focus();
    }
  });

  var focusNextCell = function focusNextCell(directionY, directionX, position) {
    var _ref4 = position || getNextCellPosition({
      grid: grid.current,
      currentCell: focusedCell.current,
      currentRow: focusedRow.current,
      directionX: directionX,
      directionY: directionY,
      shouldWrapCells: shouldWrapCells,
      shouldWrapRows: shouldWrapRows
    }),
        rowIndex = _ref4.rowIndex,
        cellIndex = _ref4.cellIndex;

    var node = getNode({
      rowIndex: rowIndex,
      cellIndex: cellIndex
    }); // can focus

    if (node) {
      node.focus();
      focusedRow.current = rowIndex;
      focusedCell.current = cellIndex;
    }
  };

  registerHotKeysHooks(hotKeys, {
    focusedCell: focusedCell,
    focusedRow: focusedRow
  });

  var defaultKeyBindings = _objectSpread({
    ArrowUp: runAll(preventDefault, function () {
      return focusNextCell(-1, 0);
    }),
    ArrowRight: runAll(preventDefault, function () {
      return focusNextCell(0, 1);
    }),
    ArrowDown: runAll(preventDefault, function () {
      return focusNextCell(1, 0);
    }),
    ArrowLeft: runAll(preventDefault, function () {
      return focusNextCell(0, -1);
    }),
    Home: runAll(preventDefault, function (e) {
      var rowIndex = focusedRow.current;

      if (e.ctrlKey) {
        rowIndex = 0;
      }

      focusNextCell(0, 0, {
        rowIndex: rowIndex,
        cellIndex: 0
      });
    }),
    End: runAll(preventDefault, function (e) {
      var rowIndex = focusedRow.current;

      if (e.ctrlKey) {
        rowIndex = grid.current.length - 1;
      }

      focusNextCell(0, 0, {
        rowIndex: rowIndex,
        cellIndex: grid.current[0].length - 1
      });
    })
  }, keyBindings);

  var nextKeyBindings = isFunction(keyBindings) ? keyBindings({
    defaultBindings: defaultKeyBindings,
    rowIndex: focusedRow.current,
    cellIndex: focusedCell.current
  }) : _objectSpread(_objectSpread({}, defaultKeyBindings), keyBindings);

  var onKeyDown = function onKeyDown(e) {
    if (e.target.tagName.toLowerCase() === 'input') return;
    var action = nextKeyBindings[e.key];
    if (isFunction(action)) action(e);
  };

  var activate = function activate(container) {
    container.addEventListener('keydown', onKeyDown, true);
  };

  var deactivate = function deactivate(container) {
    container.removeEventListener('keydown', onKeyDown, true);
  };

  useEffect(function () {
    activate(containerRef.current);
    return function () {
      deactivate(containerRef.current);
    };
  }, []);
  var valueProvider = useMemo(function () {
    return {
      register: register
    };
  }, []);
  return /*#__PURE__*/React.createElement(Provider, {
    value: valueProvider
  }, decoratedChild);
}

export default FocusGridProvider;
export { FocusGridContext };
//# sourceMappingURL=FocusGrid.js.map
